import {
  ContentfulImage,
  type ContentfulImageSchema
} from '@ecomm/contentful-components'
import classNames from 'classnames'
import React, { useCallback } from 'react'

import { useTrackingPagerClick } from './useTrackingPagerClick'

type ImageGalleryPagerProps = {
  readonly productImages: readonly ContentfulImageSchema[]
  readonly currentImage: number
  readonly onPagerClick: (index: number) => void
  readonly isModal?: boolean
}

export function ImageGalleryPager({
  productImages,
  currentImage,
  onPagerClick: onPagerClickProp,
  isModal = false
}: ImageGalleryPagerProps) {
  const trackPagerClick = useTrackingPagerClick()

  const handlePagerClick = useCallback(
    (index: number) => () => {
      onPagerClickProp(index)
      trackPagerClick(index + 1)
    },
    [onPagerClickProp, trackPagerClick]
  )

  const handlePagerFocus = useCallback(
    (index: number) => () => {
      onPagerClickProp(index)
    },
    [onPagerClickProp]
  )

  return (
    <ul
      className={classNames(
        'pl-0 list-none order-last col-span-12 mt-4 flex justify-start',
        {
          'md:order-first md:col-span-2 md:mt-0 md:flex-col md:items-start lg:order-first lg:col-span-2 lg:mt-0 lg:flex-col lg:items-center':
            !isModal
        }
      )}
      data-component="ImageGalleryPager"
      data-testid="ImageGalleryPager"
      aria-label="Product image gallery"
      role="list"
    >
      {productImages.map((image, idx) => (
        <li key={image.url}>
          <button
            className={classNames(
              'mr-2 aspect-square h-14 lg:h-14 rounded-lg border-2 border-solid p-0 transition-all hover:border-complementary-blue-100 focus:outline-none focus:ring-2 focus:ring-complementary-blue-100 overflow-hidden',
              {
                'border-neutral-dark': idx === currentImage,
                'border-white': idx !== currentImage,
                'md:mb-2 md:mr-6 md:h-full lg:mx-6': !isModal
              }
            )}
            onClick={handlePagerClick(idx)}
            onFocus={handlePagerFocus(idx)}
            aria-label={`Product thumbnail ${idx + 1} of ${productImages.length}: ${image.description}`}
          >
            <ContentfulImage
              className="h-full w-full object-contain object-center"
              {...image}
            />
          </button>
        </li>
      ))}
    </ul>
  )
}
